import React, { useState, useEffect } from 'react';
import api from '../services/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardBody, CardTitle } from 'reactstrap';
import { toast } from 'react-toastify';

const UpdateMainPage = () => {
  const { id } = useParams();
  const [subpages, setSubpages] = useState([]);
  const [selectedSubpage, setSelectedSubpage] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [image1, setImage1] = useState(null);
  const [image2, setImage2] = useState(null);
  const [existingImage1, setExistingImage1] = useState('');
  const [existingImage2, setExistingImage2] = useState('');
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      fetchMainpage();
      fetchSubpages();
    }
  }, [id]);

  const fetchMainpage = async () => {
      if (!id) return;
      try {
        const response = await api.get(`/pagedetail/get/${id}`);
        const mainpage = response.data?.data;

          if (mainpage) {
            setTitle(mainpage.title || '');
            setDescription(mainpage.description || '');
            setSelectedSubpage(mainpage.subpage_id || null);
            setExistingImage1(mainpage.image1 || null);
            setExistingImage2(mainpage.image2 || null);
            } else {
              alert("Main page data not found");
            }
          } catch (error) {
            console.error('Error fetching mainpage:', error.response?.data || error.message);
            alert('Failed to fetch mainpage data');
          }
    };

    const fetchSubpages = async () => {
      try {
        const response = await api.get('/subpageapi/get-all');
        setSubpages(response.data.data);
      } catch (error) {
        console.error('Error fetching subpages:', error);
        alert('Failed to fetch subpages');
      }
    };

  const validateImageDimensions = (file, maxWidth, maxHeight) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        if (image.width <= maxWidth && image.height <= maxHeight) {
          resolve(true);
        } else {
          resolve(false);
        }
      };
      image.onerror = () => {
        reject(new Error('Invalid image file'));
      }
      image.src = URL.createObjectURL(file);
    })
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let errors = {};

    if (!selectedSubpage) errors.selectedSubpage = 'select subpage';
    if (!title.trim()) errors.title = 'title cannot be empty'
    if (!description.trim()) errors.description = 'description cannot be empty';

    const allowedImageTypes = ['image/png', 'image/jpeg'];
    if (image1) {
      if (!allowedImageTypes.includes(image1.type)) {
        errors.image1 = 'Only PNG and JPG files are allowed';
      } else {
        const isValidImage = await validateImageDimensions(image1, 1200, 630);
        if (!isValidImage) {
          errors.image1 = 'Image1 must be under 1200px by 630px';
        }
      }
    }
    if (image2) {
      if (!allowedImageTypes.includes(image2.type)) {
        errors.image2 = 'Only PNG and JPG files are allowed';
      } else {
        const isValidImage = await validateImageDimensions(image2, 1200, 630);
        if (!isValidImage) {
          errors.image2 = 'Image2 must be under 1200px by 630px';
        }
      }
    }

    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    setErrors({});

    const formData = new FormData();
    formData.append('title', title);
    formData.append('description', description);
    formData.append('subpage_id', selectedSubpage);
    if (image1) formData.append('image1', image1);
    if (image2) formData.append('image2', image2);

    try {
      const response = await api.post(`/pagedetail/update/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.status === "success") {
        toast.success(`Mainpage updated successfully! ID: ${id}`);
                  navigate('/display-pages');
      } else {
        toast.error('Failed to update mainpage');
      }
    } catch (error) {
      console.error('Error updating mainpage:', error);
      toast.error('Error updating mainpage');
    }
  };

  const handleCancel = () => {
    navigate('/display-pages');
  };

  return (
    <div className='container mt-3 mb-5'>
      <CardTitle tag='h5' className='mb-3'>
        Mainpage List <span style={{ color: '#B0B0B0' }}>/ Edit Mainpage</span>
      </CardTitle>
      <Card>
        <CardBody>
          <form onSubmit={handleSubmit}>
            <div className="col-xl-8">
              <div className='mb-3'>
                <label className='form-label'>Select Subpage *</label>
                <select className={`form-control ${errors.selectedSubpage ? 'is-invalid' : ''}`} value={selectedSubpage} onChange={(e) => setSelectedSubpage(e.target.value)}  >
                  <option value="">Select Subpage</option>
                  {subpages.map((subpage) => (
                    <option key={subpage.id} value={subpage.id}>
                      {subpage.subpage}
                    </option>
                  ))}
                </select>
                <b>{errors.selectedSubpage && <span className='text-danger small'>{errors.selectedSubpage}</span>}</b>
              </div>
              <div className='mb-3'>
                <label className='form-label'>Title *</label>
                <input type="text" className={`form-control ${errors.title ? 'is-invalid' : ''}`} value={title} onChange={(e) => setTitle(e.target.value)}  />
                <b>{errors.title && <span className='text-danger small'>{errors.title}</span>}</b>
              </div>

              <div className='mb-3'>
                <label className='form-label'>Page Description *</label>
                <ReactQuill value={description} onChange={setDescription} className='' placeholder="Enter Page Description"  />
              </div>

              <div className='mb-3'>
                <label className='form-label'>Upload Image 1(only png and Jpg size 1200px*630px)</label>
                <input
                  type="file"
                  className={`form-control ${errors.image1 ? 'is-invalid' : ''}`}
                  onChange={(e) => setImage1(e.target.files[0])}
                />
                <b>{errors.image1 && <span className='text-danger small'>{errors.image1}</span>}</b>
                <div className='mt-2'>
                  {existingImage1 && !image1 && (
                    <img
                      style={{ width: '100px', height: '100px' }}
                      src={`${process.env.REACT_APP_IMAGE_BASE_URL}${existingImage1}`}
                      alt="Image 1"
                    />
                  )}
                </div>
              </div>

              <div className='mb-3'>
                <label className='form-label'>Upload Image 2(only png and Jpg size 1200px*630px)</label>
                <input
                  type="file"
                  className={`form-control ${errors.image2 ? 'is-invalid' : ''}`}
                  onChange={(e) => setImage2(e.target.files[0])}
                />
                <b>{errors.image2 && <span className='text-danger small'>{errors.image2}</span>}</b>
                <div className='mt-2'>
                  {existingImage2 && !image2 && (
                    <img
                      style={{ width: '100px', height: '100px' }}
                      src={`${process.env.REACT_APP_IMAGE_BASE_URL}${existingImage2}`}
                      alt="Image 2"
                    />
                  )}
                </div>
              </div>

              <div className="button-group">
                <button type="submit" className="btn btn-success submit-button">
                  Update
                </button>
                <button
                  type="button"
                  className="btn btn-danger cancel-button"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
};

export default UpdateMainPage;
